// src/pages/analytics/CustomerWeeksTab.tsx
//
// Customer Weeks — the PBI "Produce Cust. 10 Weeks" page (recipe
// customer-week-grid, flag analytics.custweeks, agent 3.20.0+). One row per
// customer with its weekly qty or $; expand a customer to see the same
// weeks per item category. Empty weeks are tinted so a category the
// customer stopped buying stands out. The filter pane applies.

import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarRange, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useAuthContext } from '../../hooks/useAuth';
import { adminApi } from '../../services/adminApi';
import { apiV2 as api } from '../../services/apiV2';
import type { CustomerWeekGridResponse } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterPane, AnalyticsSavedViews } from './filterPane';
import { useFilterPane } from './filterPaneState';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(v);

const WEEKS = [4, 8, 10, 13, 26];
const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;

// Flat export: one line per customer and per customer × category.
interface FlatRow { customer: string; category: string; values: number[]; total: number }

function exportGrid(data: CustomerWeekGridResponse, exporter: typeof exportCsv = exportCsv) {
  const rows: FlatRow[] = [];
  for (const r of data.rows) {
    rows.push({ customer: r.name, category: '(all)', values: r.values, total: r.total });
    for (const c of r.categories) rows.push({ customer: r.name, category: c.category, values: c.values, total: c.total });
  }
  const cols: CsvColumns<FlatRow> = [
    ['Customer', (r) => r.customer],
    ['Category', (r) => r.category],
    ...data.weeks.map((w, i): [string, (r: FlatRow) => number] => [w.label, (r) => r.values[i] ?? 0]),
    ['Total', (r) => r.total],
  ];
  exporter(`customer-weeks-${data.measure}`, cols, rows);
}

export function CustomerWeeksTab() {
  const { claims, activeTenant } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;
  const [weeks, setWeeks] = useState(10);
  const [measure, setMeasure] = useState<'qty' | 'dollars'>('qty');
  const [calendarOverride, setCalendarOverride] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const pane = useFilterPane();

  const resetFilters = () => { setWeeks(10); setMeasure('qty'); setCalendarOverride(null); setSearch(''); setExpanded(new Set()); };

  // Same tenant default-calendar lookup (and cache key) as the YoY tab.
  const calendarDefaultQ = useQuery({
    queryKey: ['analyticsYoy', 'default-calendar', slug],
    queryFn: () => adminApi.getConfig('analytics.default_calendar', slug ?? undefined),
    enabled: !!slug,
    staleTime: 60 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
  const calendar = calendarOverride ?? (calendarDefaultQ.data?.value === 'hebrew' ? 'hebrew' : 'gregorian');

  const q = useQuery({
    queryKey: ['analyticsCustomerWeeks', weeks, measure, calendar, pane.key],
    queryFn: () => api.getCustomerWeekGrid({ weeks, measure, calendar }, pane.params),
    enabled: !slug || calendarOverride !== null || !calendarDefaultQ.isLoading,
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const fmt = measure === 'dollars' ? fmtCur : fmtNum;
  const needle = search.trim().toLowerCase();
  const rows = (data?.rows ?? []).filter(r => !needle || r.name.toLowerCase().includes(needle));
  const toggle = (code: number) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  const cell = (v: number, strong = false) => (
    <td className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap ${
      v === 0 ? 'bg-amber-500/5 text-slate-600' : strong ? 'text-white' : 'text-slate-300'}`}>
      {v === 0 ? '–' : fmt(v)}
    </td>
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <CalendarRange size={20} /> Customer Weeks
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          What each customer buys week by week, by item category. Empty weeks are tinted.
          {data && <span className="text-slate-500"> · {data.dateFrom} → {data.dateTo}</span>}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Weeks</span>
          <div className="flex items-center gap-1.5">
            {WEEKS.map(w => <button key={w} onClick={() => setWeeks(w)} className={pill(weeks === w)}>{w}</button>)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Measure</span>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setMeasure('qty')} className={pill(measure === 'qty')}>Qty</button>
            <button onClick={() => setMeasure('dollars')} className={pill(measure === 'dollars')}>Dollars</button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Calendar</span>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setCalendarOverride('gregorian')} className={pill(calendar === 'gregorian')}>Gregorian</button>
            <button onClick={() => setCalendarOverride('hebrew')} className={pill(calendar === 'hebrew')}>Hebrew</button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Customer</span>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
              className="pl-6 pr-2 py-1.5 w-44 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
          </div>
        </div>
        <div className="flex-1" />
        <AnalyticsSavedViews
          page="customer-weeks"
          getState={() => ({ weeks, measure, calendar: calendarOverride })}
          applyState={(s) => {
            if (typeof s.weeks === 'number' && WEEKS.includes(s.weeks)) setWeeks(s.weeks);
            if (s.measure === 'qty' || s.measure === 'dollars') setMeasure(s.measure);
            setCalendarOverride(s.calendar === 'hebrew' || s.calendar === 'gregorian' ? s.calendar : null);
          }}
        />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button onClick={() => data && exportGrid(data)} disabled={!data || data.rows.length === 0}
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button onClick={() => data && exportGrid(data, exportXlsx)} disabled={!data || data.rows.length === 0}
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <FilterPane />

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load customer weeks</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-20">
                <tr className="border-b border-slate-700/50 bg-slate-800">
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[220px]">Customer / Category</th>
                  {data.weeks.map(w => (
                    <th key={w.weekStart} title={`Week of ${w.weekStart}`} className="px-2 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">{w.label}</th>
                  ))}
                  <th className="px-3 py-2.5 text-right text-slate-300 font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const open = expanded.has(r.code);
                  return (
                    <Fragment key={r.code}>
                      <tr className="group border-b border-slate-800/50 hover:bg-slate-700/20">
                        <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-1.5 text-white whitespace-nowrap">
                          <button onClick={() => toggle(r.code)} className="flex items-center gap-1.5 hover:text-blue-300 max-w-[260px]" title={r.name}>
                            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            <span className="truncate">{r.name}</span>
                            {r.quietRecentWeeks >= 2 && (
                              <span className="ml-1 px-1 rounded bg-amber-500/15 text-amber-300 text-[10px]" title="Weeks with no purchase among the last 4">
                                {r.quietRecentWeeks} quiet
                              </span>
                            )}
                          </button>
                        </td>
                        {r.values.map((v, i) => <Fragment key={i}>{cell(v, true)}</Fragment>)}
                        <td className="px-3 py-1.5 text-right text-white font-medium tabular-nums">{fmt(r.total)}</td>
                      </tr>
                      {open && r.categories.map(c => (
                        <tr key={`${r.code}-${c.category}`} className="border-b border-slate-800/30">
                          <td className="sticky left-0 z-10 bg-slate-900 px-3 py-1.5 pl-9 text-slate-400 whitespace-nowrap">{c.category}</td>
                          {c.values.map((v, i) => <Fragment key={i}>{cell(v)}</Fragment>)}
                          <td className="px-3 py-1.5 text-right text-slate-300 tabular-nums">{fmt(c.total)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
              {data.rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                    <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                    {data.totals.values.map((v, i) => (
                      <td key={i} className="px-2 py-2 text-right text-white tabular-nums whitespace-nowrap">{fmt(v)}</td>
                    ))}
                    <td className="px-3 py-2 text-right text-white tabular-nums">{fmt(data.totals.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {data.rows.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No sales in these weeks</div>}
          <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
            {data.truncated
              ? `Top ${data.rows.length} of ${data.customerCount} customers by ${measure === 'dollars' ? 'dollars' : 'quantity'}`
              : `${data.customerCount} customers`}
            {needle && ` · ${rows.length} matching "${search.trim()}"`}
          </div>
        </div>
      )}
    </div>
  );
}
